import { mapAggregate } from '../../../util/index.js';
import { startOf } from './dates.js';
import { isActive, prefixShares, runsOf, } from './ledger.js';
import { dayIndex, shadowIndexOf, voidedAtOf } from './streams.js';
// Whether `row` goes on where `last` ends, at the same rate and balances.
const continuesRow = (last, row) => last.to === row.from &&
    last.interest === row.interest &&
    last.referenceVoucherBalance === row.referenceVoucherBalance &&
    last.capitalizedInterestBalance === row.capitalizedInterestBalance;
export const addRow = (interest, rowIds, row) => {
    const lastId = rowIds === null ? interest.lastMainRow : (rowIds[rowIds.length - 1] ?? -1);
    const last = lastId === -1 ? undefined : interest.rows[lastId];
    if (last !== undefined && continuesRow(last, row)) {
        last.to = row.to;
        return lastId;
    }
    interest.rows.push({ ...row });
    const id = interest.rows.length - 1;
    if (rowIds === null) {
        interest.visibleRowIds.push(id);
        interest.lastMainRow = id;
    }
    else {
        rowIds.push(id);
    }
    return id;
};
// The days of a tranche that have not expired: all of them, or, once
// expired or irrecoverable, those its paid prefix covers (the last one
// perhaps partly). What D30 took from a day, or left of it, says nothing of
// whether it expired.
const historyPieces = (ledger, tranche) => {
    if (tranche.amount === 0) {
        // Reinstated interest that expired before its period was voided was
        // never debt, so it is left empty.
        return tranche.expiredAt === null
            ? []
            : [{ from: tranche.from, to: tranche.to, expired: true }];
    }
    if (tranche.reason === null) {
        return [{ from: tranche.from, to: tranche.to, expired: false }];
    }
    if (tranche.expiredAt === null && ledger.closed !== tranche.reason) {
        return [];
    }
    const paidTo = tranche.paid === 0
        ? tranche.from
        : Math.min(tranche.to, tranche.from + Math.ceil(tranche.paid / tranche.perDay));
    return [
        { from: tranche.from, to: paidTo, expired: false },
        { from: paidTo, to: tranche.to, expired: true },
    ].filter((piece) => piece.from < piece.to);
};
// The parts of a row not all expired, split where that changes, as v1's
// limitation window did. A day partly paid and partly expired counts as paid;
// a day of no piece goes with the days before it.
const unexpiredSpans = (ledger, row, tranches) => {
    // Where pieces start and end: +1 or -1 to the count of each kind.
    const changes = tranches
        .flatMap((tranche) => historyPieces(ledger, tranche))
        .flatMap((piece) => [
        { day: piece.from, expired: piece.expired, count: 1 },
        { day: piece.to, expired: piece.expired, count: -1 },
    ])
        .toSorted((a, b) => a.day - b.day);
    const runs = [];
    let unexpired = 0;
    let expired = 0;
    let next = 0;
    while (next < changes.length) {
        const { day } = changes[next];
        for (; next < changes.length && changes[next].day === day; next++) {
            if (changes[next].expired) {
                expired += changes[next].count;
            }
            else {
                unexpired += changes[next].count;
            }
        }
        if (unexpired === 0 && expired === 0) {
            continue;
        }
        const allExpired = unexpired === 0;
        if (runs.at(-1)?.expired !== allExpired) {
            runs.push({
                from: runs.length === 0 ? row.from : day,
                expired: allExpired,
            });
        }
    }
    if (runs.length === 0) {
        return [row];
    }
    return runs.flatMap((run, index) => run.expired
        ? []
        : [{ from: run.from, to: runs[index + 1]?.from ?? row.to }]);
};
const clipSpan = (span, within) => {
    const from = Math.max(span.from, within.from);
    const to = Math.min(span.to, within.to);
    return from < to ? { from, to } : null;
};
// The days of `span` outside `spans`, which are ordered and apart.
const withoutSpans = (span, spans) => {
    const rest = [];
    let from = span.from;
    for (const other of spans) {
        const before = clipSpan({ from, to: other.from }, span);
        if (before !== null) {
            rest.push(before);
        }
        from = Math.max(from, other.to);
    }
    const after = clipSpan({ from, to: span.to }, span);
    if (after !== null) {
        rest.push(after);
    }
    return rest;
};
// What of a tranche the days of `span` hold of its first `prefix`, spread
// evenly over its days.
const prefixIn = (tranche, span, prefix) => {
    const days = clipSpan(span, tranche);
    if (days === null) {
        return 0;
    }
    const { from, to } = days;
    return Math.max(0, Math.min(prefix, tranche.perDay * (to - tranche.from)) -
        tranche.perDay * (from - tranche.from));
};
// What a tranche's days in `span` hold, net of write-offs: in all, paid, and
// unpaid but acknowledged, i.e. on the days before `acknowledgedBefore`. Paid
// is a prefix of the net.
const amountsIn = (tranche, span, acknowledgedBefore) => {
    const net = tranche.amount - tranche.writtenOff;
    const acknowledgedSpan = {
        from: span.from,
        to: Math.min(span.to, acknowledgedBefore),
    };
    return {
        amount: prefixIn(tranche, span, net),
        paid: prefixIn(tranche, span, tranche.paid),
        acknowledged: prefixIn(tranche, acknowledgedSpan, net) -
            prefixIn(tranche, acknowledgedSpan, tranche.paid),
    };
};
// Compound interest shows the capitalized base what is left accrued on: what
// D30 later took from it is left out, by day, as it applied. In whole øre.
const capitalizedOn = (interest, row) => {
    const capitalized = row.capitalizedInterestBalance;
    if (capitalized === undefined || interest.compoundingType === null) {
        return null;
    }
    const removed = row.reapplied === true ? null : interest.baseRemoved;
    // A capitalized base is debt, never a credit: what D30 could not write off
    // in whole øre leaves a fraction behind, which is not interest.
    return (day) => {
        const rounded = Math.round(Math.min(0, capitalized + (removed?.[dayIndex(interest, day)] ?? 0)));
        return rounded === 0 ? 0 : rounded;
    };
};
// Splits a span into the runs of days with one capitalized base
const byCapitalized = (span, capitalized) => {
    const parts = [];
    for (let day = span.from; day < span.to; day++) {
        const value = capitalized(day);
        const last = parts.at(-1);
        if (last?.capitalized === value) {
            last.to = day + 1;
        }
        else {
            parts.push({ from: day, to: day + 1, capitalized: value });
        }
    }
    return parts;
};
// Neighbouring rows the history split apart join where nothing differs, such
// as days on which the base changed only by what later expired.
export const joinRows = (rows) => rows.reduce((joined, row) => {
    const last = joined.at(-1);
    if (last !== undefined &&
        last.reapplied === undefined &&
        row.reapplied === undefined &&
        continuesRow(last, row)) {
        last.to = row.to;
        last.amount += row.amount;
        last.paid += row.paid;
        last.acknowledged += row.acknowledged;
    }
    else {
        joined.push({ ...row });
    }
    return joined;
}, []);
// The first step a covered day could be claimed in: the statement shows it
// accrue there (D31).
const claimableFrom = (interest, day) => interest.claimSteps.find((step) => step.to > day && step.boundary > day)
    ?.from ?? day;
// What was written off for `reason`, where the statement booked it.
// Reinstated interest is booked when its period is voided, so with the day
// before that. Covered interest is booked as it becomes claimable, and counts
// as far as it was claimable when it was written off (D31): its oldest days.
export const writeOffAccruals = (entry, reason) => {
    const interest = entry.interest;
    const covered = entry.ceilingCovered;
    let claimable = (reason === 'expired'
        ? entry.voucher.expiredAmount
        : entry.voucher.irrecoverableAmount) ?? 0;
    const accruals = [];
    for (const tranche of entry.ledger.tranches.flatMap(runsOf)) {
        if (!isActive(tranche) ||
            tranche.reason !== reason ||
            tranche.writtenOff === 0) {
            continue;
        }
        const shadowIndex = shadowIndexOf(interest, tranche.stream);
        if (shadowIndex === 0 && !covered) {
            accruals.push({
                from: startOf(tranche.from),
                to: startOf(tranche.to),
                perDay: tranche.perDay,
                paid: tranche.paid,
                writtenOff: tranche.writtenOff,
            });
            continue;
        }
        const bookedOn = shadowIndex === 0 ? null : voidedAtOf(interest, shadowIndex) - 1;
        for (let day = tranche.from; day < tranche.to; day++) {
            const shares = prefixShares(tranche, (day - tranche.from) * tranche.perDay, tranche.perDay);
            const { paid } = shares;
            let { writtenOff } = shares;
            if (covered) {
                writtenOff = Math.min(writtenOff, claimable);
                claimable -= writtenOff;
            }
            if (writtenOff <= 0) {
                continue;
            }
            const booked = bookedOn ?? day;
            const shown = covered ? claimableFrom(interest, day) : booked;
            accruals.push(shown > booked
                ? {
                    from: startOf(shown),
                    to: startOf(shown + 1),
                    perDay: writtenOff,
                    paid: 0,
                    writtenOff,
                }
                : {
                    from: startOf(booked),
                    to: startOf(booked + 1),
                    perDay: tranche.perDay,
                    paid,
                    writtenOff,
                });
        }
    }
    return accruals;
};
export const history = (entry) => {
    const interest = entry.interest;
    const tranchesByRow = mapAggregate(entry.ledger.tranches
        .flatMap(runsOf)
        .filter((tranche) => tranche.row !== -1 && !tranche.pending), (tranche) => tranche.row);
    // Reinstated interest is only acknowledged from its voidedAt (D8). Keyed by
    // the row each tranche accrued in, not the row it is shown in: a voided
    // period's row also shows what was charged on its days, which is
    // acknowledged as usual.
    const { acknowledgedAt } = entry.caseState;
    const acknowledgedBeforeByRow = new Map(interest.shadows.flatMap((shadow) => shadow.rowIds.map((id) => [
        id,
        acknowledgedAt >= shadow.voidedAt
            ? acknowledgedAt
            : Number.NEGATIVE_INFINITY,
    ])));
    const rowParts = (row, spans, tranches) => {
        const capitalized = capitalizedOn(interest, row);
        return spans
            .flatMap((span) => capitalized === null
            ? [{ ...span, capitalized: row.capitalizedInterestBalance }]
            : byCapitalized(span, capitalized))
            .map((part) => {
            const amounts = tranches.map((tranche) => amountsIn(tranche, part, acknowledgedBeforeByRow.get(tranche.row) ?? acknowledgedAt));
            const entryRow = {
                ...row,
                from: part.from,
                to: part.to,
                capitalizedInterestBalance: part.capitalized,
                amount: amounts.reduce((sum, { amount }) => sum - amount, 0),
                paid: amounts.reduce((sum, { paid }) => sum - paid, 0),
                acknowledged: amounts.reduce((sum, { acknowledged }) => sum - acknowledged, 0),
            };
            if (part.capitalized === undefined) {
                delete entryRow.capitalizedInterestBalance;
            }
            if (row.reapplied === undefined) {
                delete entryRow.reapplied;
            }
            return entryRow;
        });
    };
    // Where a voided period's reinstated interest expired, its window shows
    // the rate charged, in date order among the rows charged.
    const chargedParts = [];
    const rows = interest.visibleRowIds.flatMap((id) => {
        const row = interest.rows[id];
        const tranches = tranchesByRow.get(id) ?? [];
        const chargedIds = interest.chargedRowIds.get(id) ?? [];
        const spans = unexpiredSpans(entry.ledger, row, tranches);
        if (chargedIds.length === 0) {
            return rowParts(row, spans, tranches);
        }
        const charged = chargedIds.map((chargedId) => ({
            row: interest.rows[chargedId],
            tranches: tranchesByRow.get(chargedId) ?? [],
        }));
        for (const part of charged) {
            if (part.tranches.length === 0) {
                continue;
            }
            for (const rest of withoutSpans(part.row, spans)) {
                chargedParts.push(...rowParts(part.row, unexpiredSpans(entry.ledger, rest, part.tranches).flatMap((span) => clipSpan(span, rest) ?? []), part.tranches));
            }
        }
        return rowParts(row, spans, tranches.concat(charged.flatMap((part) => part.tranches)));
    });
    for (const part of chargedParts.toSorted((a, b) => a.from - b.from)) {
        const before = rows.findLastIndex((row) => row.reapplied !== true && row.from < part.from);
        rows.splice(before + 1, 0, part);
    }
    return joinRows(rows).map((row) => ({
        ...row,
        from: startOf(row.from),
        to: startOf(row.to),
    }));
};
//# sourceMappingURL=history.js.map